/**
 * Offer checker — data layer.
 *
 * Thin typed wrappers over POST /api/v1/offer-checker/run. The backend
 * validates a tracking URL + postback template and returns a diagnostic
 * report: URL status, macro substitution preview, postback readiness.
 *
 * Raw `fetch()` is confined to this file per dash contract rule D6.
 * Base URL + tenant header follow the same convention as `@/api-client`.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Env + URL resolution
// ---------------------------------------------------------------------------

const RAW_BASE = (import.meta.env.VITE_API_URL ?? "http://localhost:8787").replace(/\/+$/, "");
const API_BASE = `${RAW_BASE}/api/v1`;
const TENANT_ID = import.meta.env.VITE_TENANT_ID ?? "demo";

// ---------------------------------------------------------------------------
// Macro catalogue — mirrors apps/api/src/lib/offer-checker.ts KNOWN_MACROS.
// Duplicated here so the preview panel can render a substitution table
// without a network call. Keep in sync manually when the API changes.
// ---------------------------------------------------------------------------

export const KNOWN_MACROS = [
	"click_id",
	"payout",
	"currency",
	"campaign_id",
	"offer_id",
	"affiliate_id",
	"source",
	"sub1",
	"sub2",
	"sub3",
	"sub4",
	"sub5",
	"country",
	"device",
	"os",
	"event",
	"timestamp",
] as const;

export type KnownMacro = (typeof KNOWN_MACROS)[number];

/** Sample substitution values used to preview how the postback will render. */
export const MACRO_SAMPLES: Record<KnownMacro, string> = {
	click_id: "clk_0c4f91a3",
	payout: "1.25",
	currency: "USD",
	campaign_id: "camp_4821",
	offer_id: "offer_91",
	affiliate_id: "aff_217",
	source: "web",
	sub1: "sub1_val",
	sub2: "sub2_val",
	sub3: "sub3_val",
	sub4: "sub4_val",
	sub5: "sub5_val",
	country: "US",
	device: "mobile",
	os: "ios",
	event: "install",
	timestamp: "1734220800",
};

// ---------------------------------------------------------------------------
// API types — match apps/api/src/lib/offer-checker.ts OfferCheckRunView
// ---------------------------------------------------------------------------

export type OfferCheckStatus = "pass" | "warn" | "fail";

export type OfferCheckIssue =
	| "url_invalid"
	| "url_unreachable"
	| "url_non_2xx_3xx"
	| "missing_click_id"
	| "invalid_macros"
	| "empty_postback"
	| "empty_url";

export interface OfferCheckRunView {
	id: string;
	campaign_id: string;
	status: OfferCheckStatus;
	url: string;
	url_status: number | null;
	url_ok: boolean;
	url_error: string | null;
	postback_template: string;
	postback_has_click_id: boolean;
	macros_found: string[];
	macros_invalid: string[];
	issues: OfferCheckIssue[];
	created_at: number;
}

export interface RunCheckBody {
	campaignId: string;
	url: string;
	postbackTemplate: string;
}

export interface RunCheckResponse {
	run: OfferCheckRunView;
}

// ---------------------------------------------------------------------------
// Issue copy — human-readable labels for the report panel.
// ---------------------------------------------------------------------------

export const ISSUE_LABELS: Record<OfferCheckIssue, string> = {
	url_invalid: "Tracking URL is not a valid http/https URL.",
	url_unreachable: "Tracking URL could not be reached.",
	url_non_2xx_3xx: "Tracking URL returned a non-2xx/3xx status.",
	missing_click_id: "Postback template is missing {click_id}.",
	invalid_macros: "Postback contains unknown macro names.",
	empty_postback: "Postback template is empty.",
	empty_url: "Tracking URL is empty.",
};

// ---------------------------------------------------------------------------
// Form schema — validated before we hit the network.
// ---------------------------------------------------------------------------

export const runCheckSchema = z.object({
	campaignId: z.string().trim().min(1, "Campaign ID is required."),
	url: z
		.string()
		.trim()
		.min(1, "Tracking URL is required.")
		.refine(
			(value) => {
				try {
					const u = new URL(value);
					return u.protocol === "http:" || u.protocol === "https:";
				} catch {
					return false;
				}
			},
			{ message: "Enter a valid http or https URL." },
		),
	postbackTemplate: z
		.string()
		.trim()
		.min(1, "Postback template is required."),
});

export type RunCheckFormValues = z.infer<typeof runCheckSchema>;

// ---------------------------------------------------------------------------
// HTTP plumbing
// ---------------------------------------------------------------------------

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function defaultHeaders(extra?: Record<string, string>): Record<string, string> {
	return {
		"x-tenant-id": TENANT_ID,
		accept: "application/json",
		...extra,
	};
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** POST /api/v1/offer-checker/run — run a single diagnostic check. */
export async function runCheck(body: RunCheckBody): Promise<OfferCheckRunView> {
	const res = await fetch(`${API_BASE}/offer-checker/run`, {
		method: "POST",
		headers: defaultHeaders({ "content-type": "application/json" }),
		credentials: "include",
		body: JSON.stringify(body),
	});
	const data = await parseOrThrow<RunCheckResponse>(res);
	return data.run;
}

/**
 * Optional — list campaigns for the dropdown. The /campaigns endpoint may or
 * may not exist depending on sprint; callers must handle empty gracefully.
 * We intentionally swallow errors so missing backend never blocks the form.
 */
export interface CampaignOption {
	id: string;
	name: string;
}

interface CampaignsListResponse {
	items?: Array<{ id: unknown; name?: unknown }>;
}

export async function listCampaigns(): Promise<CampaignOption[]> {
	try {
		const res = await fetch(`${API_BASE}/campaigns`, {
			method: "GET",
			headers: defaultHeaders(),
			credentials: "include",
		});
		if (!res.ok) return [];
		const data = (await res.json().catch(() => null)) as CampaignsListResponse | null;
		const items = Array.isArray(data?.items) ? data?.items ?? [] : [];
		return items
			.map((row) => ({
				id: typeof row.id === "string" ? row.id : String(row.id ?? ""),
				name: typeof row.name === "string" ? row.name : String(row.name ?? ""),
			}))
			.filter((row) => row.id.length > 0);
	} catch {
		return [];
	}
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const offerCheckerQueryKeys = {
	all: ["offer-checker"] as const,
	runs: () => [...offerCheckerQueryKeys.all, "runs"] as const,
	campaigns: () => [...offerCheckerQueryKeys.all, "campaigns"] as const,
	mutation: (op: string) => [...offerCheckerQueryKeys.all, "mutation", op] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers for the preview panel
// ---------------------------------------------------------------------------

const MACRO_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

export interface MacroPreviewRow {
	name: string;
	known: boolean;
	sample: string;
}

/** Substitute {macro} tokens in a postback template with MACRO_SAMPLES values. */
export function substituteMacros(template: string): string {
	return template.replace(MACRO_RE, (_raw, name: string) => {
		if ((KNOWN_MACROS as readonly string[]).includes(name)) {
			return MACRO_SAMPLES[name as KnownMacro];
		}
		return `{${name}}`;
	});
}

/** Build a preview table for a postback template's macros. */
export function macroPreviewRows(template: string): MacroPreviewRow[] {
	const seen = new Set<string>();
	const rows: MacroPreviewRow[] = [];
	MACRO_RE.lastIndex = 0;
	let match: RegExpExecArray | null;
	while ((match = MACRO_RE.exec(template)) !== null) {
		const name = match[1]!;
		if (seen.has(name)) continue;
		seen.add(name);
		const known = (KNOWN_MACROS as readonly string[]).includes(name);
		rows.push({
			name,
			known,
			sample: known ? MACRO_SAMPLES[name as KnownMacro] : "(unknown)",
		});
	}
	return rows;
}
